import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Shield, Sparkles, ArrowRight } from "lucide-react";

/**
 * v0.2 Plan 06-03 — OnboardingTour (personalized)
 *
 * Four-step overlay tour for first-time Clerk signups. Lives over /scope-guard
 * when ?tutorial=1 is present and user.tutorial_completed === false.
 *
 * Step 1 — centered welcome overlay, dismissable with "Show me"
 * Step 2 — anchored note over the Scope Guard form (personalized per useCase);
 *          ScopeGuard auto-fires Generate 1.5s after mount. Step auto-advances
 *          on response-ready.
 * Step 3 — gain-framed credit reveal anchored top, advances to Step 4
 * Step 4 — capabilities preview (Change Orders / Proposals / Client portal),
 *          dismissable with "Take it for a spin" -> persists tutorial_completed
 *          + nav to /dashboard.
 *
 * Decoupled from the legacy ProductTutorial (Help-icon re-trigger), which
 * continues to fire via its own localStorage gate.
 */

// Step 2 copy keyed on user.primary_use_case (captured at /welcome).
// Differentiation is mainly framing — actual generated doc varies by
// outputType (revision_policy vs scope) which ScopeGuard wires from
// tutorial.useCase via TUTORIAL_PREFILL.
const STEP_2_COPY = {
  scope_creep: {
    title: "Real scope-creep moment: client wants revision #5 on a 2-round contract.",
    sub: "We pre-filled Branding + Revision Policy + 2 rounds. Generating now — watch the credit pill in the header.",
  },
  late_pay: {
    title: "Real late-pay moment: invoice is 30 days overdue and the client has gone quiet.",
    sub: "We pre-filled Consulting + Revision Policy. Generating a firm-but-warm follow-up policy — watch the credit pill.",
  },
  brief_changes: {
    title: "Real brief change: client wants to pivot the entire deliverable mid-project.",
    sub: "We pre-filled Branding + Scope Definition. Generating your re-scope doc now — watch the credit pill.",
  },
  pricing_pushback: {
    title: "Real pricing-pushback: client says budget shrank — wants the same scope for less.",
    sub: "We pre-filled Branding + Scope Definition. Generating a budget-realigned scope — watch the credit pill.",
  },
  exploring: {
    title: "Real designer scenario: client just asked for revision #5 on a project priced for 2.",
    sub: "We pre-filled Branding + Revision Policy. Generating your revision-policy doc now — watch the credit pill in the header.",
  },
};

function getStep2(useCase) {
  return STEP_2_COPY[useCase] || STEP_2_COPY.exploring;
}

export default function OnboardingTour({ step, onAdvance, onComplete, useCase }) {
  // Lock body scroll while the tour is active so the spotlight doesn't drift.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  if (step === 1) {
    return (
      <div
        className="fixed inset-0 z-[100] bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4"
        data-testid="onboarding-tour-step-1"
      >
        <div className="max-w-md w-full bg-slate-900 border border-slate-700 rounded-2xl p-8 shadow-2xl">
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Shield className="w-7 h-7 text-white" />
            </div>
          </div>
          <h2 className="text-xl font-semibold text-slate-50 text-center">
            Welcome to ClientKit.
          </h2>
          <p className="text-slate-300 text-sm mt-3 text-center leading-relaxed">
            Let's show you the wedge that matters — Scope Guard. We'll generate a
            real response together so you can feel what it actually does.
          </p>
          <Button
            onClick={() => onAdvance(2)}
            className="w-full mt-6 bg-indigo-600 hover:bg-indigo-500 text-white"
            data-testid="onboarding-tour-show-me"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Show me
          </Button>
        </div>
      </div>
    );
  }

  if (step === 2) {
    // Personalized to user.primary_use_case captured at /welcome.
    const copy = getStep2(useCase);
    return (
      <div
        className="fixed top-0 left-0 right-0 z-[100] pointer-events-none"
        data-testid="onboarding-tour-step-2"
        data-use-case={useCase || "exploring"}
      >
        <div className="max-w-2xl mx-auto mt-6 pointer-events-auto">
          <div className="bg-indigo-950/95 border border-indigo-500/50 rounded-2xl p-5 shadow-2xl backdrop-blur">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-indigo-300 animate-pulse" />
              </div>
              <div className="flex-1">
                <h3 className="text-slate-50 font-medium text-sm">
                  {copy.title}
                </h3>
                <p className="text-slate-300 text-xs mt-1.5 leading-relaxed">
                  {copy.sub}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 3) {
    // Gain-framed credit reveal — advances to Step 4 instead of completing.
    return (
      <div
        className="fixed inset-0 z-[100] bg-slate-950/70 backdrop-blur-sm flex items-start justify-center p-4 pt-24"
        data-testid="onboarding-tour-step-3"
      >
        <div className="max-w-md w-full bg-slate-900 border border-amber-500/40 rounded-2xl p-7 shadow-2xl">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-medium mb-4">
              <Sparkles className="w-3 h-3" />
              Your wedge moment
            </div>
            <h2 className="text-lg font-semibold text-slate-50">
              That's Scope Guard.
            </h2>
            <p className="text-slate-300 text-sm mt-3 leading-relaxed">
              You've got <strong className="text-slate-100">2 more on the house</strong> — try a different client situation, or copy this one straight into your reply. After that, it's <strong className="text-slate-100">$29/mo for unlimited</strong>.
            </p>
            <Button
              onClick={() => onAdvance(4)}
              className="w-full mt-6 bg-indigo-600 hover:bg-indigo-500 text-white"
              data-testid="onboarding-tour-step-3-next"
            >
              What else is here?
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 4) {
    // NEW — capabilities preview. Three items, tight one-liners, single CTA.
    return (
      <div
        className="fixed inset-0 z-[100] bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4"
        data-testid="onboarding-tour-step-4"
      >
        <div className="max-w-lg w-full bg-slate-900 border border-slate-700 rounded-2xl p-8 shadow-2xl">
          <h2 className="text-xl font-semibold text-slate-50 text-center">
            Three more surfaces, same idea.
          </h2>
          <p className="text-slate-400 text-sm mt-2 text-center">
            Each one protects a different part of the relationship. They live in the sidebar.
          </p>
          <div className="mt-6 space-y-3">
            <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-4">
              <h3 className="text-slate-100 font-medium text-sm">Change Orders</h3>
              <p className="text-slate-400 text-xs mt-1">Every "one quick thing" gets logged, priced, and confirmed before you touch it.</p>
            </div>
            <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-4">
              <h3 className="text-slate-100 font-medium text-sm">Proposals + contracts</h3>
              <p className="text-slate-400 text-xs mt-1">Win the work with polished proposals. Close it with e-signed contracts.</p>
            </div>
            <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-4">
              <h3 className="text-slate-100 font-medium text-sm">Client portal</h3>
              <p className="text-slate-400 text-xs mt-1">Your clients log in to one place — your name on the door.</p>
            </div>
          </div>
          <Button
            onClick={onComplete}
            className="w-full mt-6 bg-indigo-600 hover:bg-indigo-500 text-white"
            data-testid="onboarding-tour-step-4-complete"
          >
            Take it for a spin
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
